// @ts-strict-ignore
import type { AccessTokenProvider, ApiResponse } from '@empathyco/playboard-core-domain'
import type { ContextProvider } from '../providers'
import { CONTAINER } from '../config'
import { TYPES } from '../config/inversify.types'

export class ApiService {
  protected readonly REQUEST_HEADERS = {
    AUTHORIZATION: 'Authorization',
    CONTENT_TYPE: 'Content-Type',
  }

  protected readonly CONTENT_TYPES = {
    JSON: 'application/json',
  }

  protected accessTokenProvider: AccessTokenProvider
  protected contextProvider: ContextProvider

  public constructor(protected readonly BASE_URL: string) {
    this.accessTokenProvider = CONTAINER.get<AccessTokenProvider>(TYPES.AccessTokenProvider)
    this.contextProvider = CONTAINER.get<ContextProvider>(TYPES.ContextProvider)
  }

  protected async fetch<DataType = any>(
    endpointUrl: string,
    requestParams?: Record<string, any> | string,
    requestInit: RequestInit = {},
    addAccessToken = false,
  ): Promise<ApiResponse<DataType>> {
    const accessToken = addAccessToken && (await this.accessTokenProvider())
    requestInit.headers = {
      ...requestInit.headers,
      ...(!!accessToken && {
        [this.REQUEST_HEADERS.AUTHORIZATION]: this.encodeAccessToken(accessToken),
      }),
    }
    return new Promise((resolve, reject) => {
      fetch(this.getRequestUrl(endpointUrl, requestParams), requestInit)
        .then((response: Response) => {
          const { status } = response
          if (status === 204) {
            resolve({ status })
          } else {
            response
              .json()
              .then(jsonResponse => {
                if (response.ok) {
                  resolve({
                    status,
                    data: jsonResponse,
                  })
                } else {
                  reject({
                    status,
                    data: jsonResponse,
                  })
                }
              })
              .catch(() => {
                if (response.ok) {
                  resolve({ status })
                } else {
                  reject({ status })
                }
              })
          }
        })
        .catch(reject)
    })
  }

  protected replaceUrlParams(url: string, params: Record<string, string>): string {
    return Object.entries(params).reduce((result: string, [key, value]) => {
      return result.replace(`{${key}}`, value)
    }, url)
  }

  protected encodeRequestBody(body: Record<string, any>, contentType: string): BodyInit {
    const encodeFn = {
      [this.CONTENT_TYPES.JSON]: () =>
        JSON.stringify(body, (key, value) => (typeof value === 'undefined' ? null : value)),
    }[contentType]
    return encodeFn ? encodeFn() : undefined
  }

  private encodeAccessToken(accessToken: string): string {
    return `Bearer ${accessToken}`
  }

  private getRequestUrl(
    endpointUrl: string,
    params: Record<string, string | number | boolean> | string = {},
  ): string {
    let paramsUrl = ''
    if (typeof params === 'object') {
      Object.entries(params).forEach(([paramId, paramValue], index: number) => {
        paramsUrl += `${index === 0 ? '?' : '&'}${paramId}=${encodeURIComponent(paramValue)}`
      })
    } else {
      paramsUrl += `/${params}`
    }
    let endpointSeparator = ''
    if (!endpointUrl.startsWith('/')) {
      endpointSeparator = '/'
    }
    return `${this.BASE_URL}${endpointSeparator}${endpointUrl}${paramsUrl}`
  }
}
